import React, { useEffect, useState, useCallback } from 'react';
import './HelpPage.scss';
import userGuide from './user-guide.pdf'; // <-- put the PDF in this folder

const TOPICS = [
  { key: 'login',              title: 'Login Process',                blurb: 'How to sign in securely and access your planning workspace.' },
  { key: 'dashboard',          title: 'Dashboard',                    blurb: 'Overview of stats, shortcuts, and what to check first.' },
  { key: 'add-guests',         title: 'Add Guests',                   blurb: 'Add guests individually or import in bulk—quick and tidy.' },
  { key: 'couple-lists',       title: 'Groom’s & Bride’s Guest List', blurb: 'Keep lists organized by side while viewing totals together.' },
  { key: 'assign-tables',      title: 'Assign Table Numbers',         blurb: 'Plan table layouts and assign seats with confidence.' },
  { key: 'pending',            title: 'Pending Guests',               blurb: 'Monitor invites awaiting response and follow up smartly.' },
  { key: 'not-participating',  title: 'Not Participating Guests',     blurb: 'Track declines gracefully and keep counts accurate.' },
  { key: 'change-status',      title: 'Changing Guest Status',        blurb: 'Update RSVP status and notes with one click.' },
  { key: 'liquor-count',       title: 'Liquor Count',                 blurb: 'Summarize preferences to plan bar quantities easily.' },
];

const MODAL_CONTENT = {
  login: (
    <>
      <p>
        To access your Plan My Guest Dashboard, you’ll receive a secure login URL, along with your email
        address and password. Simply open the provided link, enter your credentials, and you’ll be directed
        to your personalized dashboard where you can start managing your guests and wedding website.
      </p>
      <p>
        Please keep your login details confidential and do not share your credentials with anyone, as they
        provide access to your private event information and guest data.
      </p>
      <p>
        If you ever forget your password or experience login issues, don’t worry. Just reach out to us, and
        we’ll be happy to help you regain access quickly.
      </p>
    </>
  ),
  dashboard: (
    <>
      <p>
        The Dashboard is the heart of your wedding management system where all your guest-related activities
        come together in one simple, elegant view. From adding guests to tracking RSVPs, assigning tables,
        and managing attendance, the dashboard gives you real-time control and insights about your entire event.
      </p>
      <p>
        Whether you’re the bride, groom, or planner, everything you need is right here—beautifully organized
        and easy to use.
      </p>
    </>
  ),
  'add-guests': (
    <>
      <p>
        The Add Guests feature allows you to onboard guests efficiently by organizing them under their family name.
        For example, if you invite the Fernando family, you can add all members under that single family group such
        as Mr. Fernando, Mrs. Fernando, and Anushka Fernando.
      </p>
      <p>
        When adding each guest, you can also specify whether the guest is consuming liquor or not. This helps you keep
        an accurate liquor count, ensuring your catering and bar arrangements are planned perfectly.
      </p>
      <p>
        This grouping system makes managing invitations and RSVPs much easier. Instead of searching for each person
        individually, you can simply search the family name, and the dashboard will display all members within that family.
        This approach saves time and ensures no one is missed when updating RSVP statuses, assigning tables, or tracking attendance.
      </p>
    </>
  ),
  'couple-lists': (
    <>
      <p>
        View and manage all guests added under the groom’s and bride’s side. You can view their information, delete guests,
        and see the state of each guest from this section.
      </p>
      <p>
        This section helps the groom’s and bride’s family stay organized and easily track who’s invited, confirmed,
        and not participating.
      </p>
    </>
  ),
  'assign-tables': (
    <>
      <p>
        Simplify your seating arrangements by assigning table numbers directly from the dashboard. This section only shows
        the guests who have confirmed their participation in the wedding.
      </p>
      <p>
        Each guest can be linked to a table, allowing you to create balanced, well-organized seating plans that can be updated anytime.
      </p>
    </>
  ),
  pending: (
    <>
      <p>
        This section shows all guests who haven’t responded to your RSVP yet. It helps you follow up easily, ensuring no one
        is missed before your final headcount.
      </p>
    </>
  ),
  'not-participating': (
    <>
      <p>
        Here you can view all guests who have declined the invitation or are unable to attend. Keeping this list separate
        helps maintain an accurate total and simplifies catering and seating adjustments.
      </p>
    </>
  ),
  'change-status': (
    <>
      <p>
        You can update a guest’s status at any time—for example, from Pending to Participation Confirmed or Not Attending.
        These updates are reflected instantly in your dashboard statistics, keeping your numbers accurate in real time.
      </p>
    </>
  ),
  'liquor-count': (
    <>
      <p>
        Easily track the number of guests who will be taking liquor. This feature helps you plan catering and bar arrangements
        more efficiently by showing a clear count of liquor vs. non-liquor guests.
      </p>
    </>
  ),
};

const HelpPage = () => {
  const [openKey, setOpenKey] = useState(null);
  const current = TOPICS.find((t) => t.key === openKey);

  const openModal = useCallback((key) => setOpenKey(key), []);
  const closeModal = useCallback(() => setOpenKey(null), []);

  // ESC to close
  useEffect(() => {
    if (!openKey) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey, closeModal]);

  return (
    <main className="page page--help" aria-labelledby="help-title">
      <div className="page__inner">
        <h1 id="help-title" className="page__title">Help & Support</h1>

        {/* Download button — uses same primary styling class */}
        <div className="ug-wrap" style={{ margin: '8px 0 14px 0' }}>
          <a
            className="help-card__btn ug-btn"
            href={userGuide}
            download
            target="_blank"
            rel="noopener"
          >
            Click to Download User Guide
          </a>
        </div>

        <p className="page__intro">
          We’re friendly and flexible. Our goal is your happiness. Pick a topic to learn more,
          or contact us anytime.
        </p>

        <section className="help-grid" aria-label="Help topics">
          {TOPICS.map(({ key, title, blurb }) => (
            <article key={key} className="help-card">
              <h2 className="help-card__title">{title}</h2>
              <p className="help-card__body">{blurb}</p>
              <div className="help-card__actions">
                <button
                  className="help-card__btn"
                  type="button"
                  onClick={() => openModal(key)}
                  aria-haspopup="dialog"
                  aria-controls="help-modal"
                >
                  Open
                </button>
              </div>
            </article>
          ))}
        </section>

        <a href="/" className="page__back">← Back to Home</a>
      </div>

      {/* Modal */}
      {current && (
        <div
          id="help-modal"
          className="help-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-modal-title"
          onClick={closeModal}
        >
          <div className="help-modal__inner" onClick={(e) => e.stopPropagation()}>
            <header className="help-modal__header">
              <h3 id="help-modal-title" className="help-modal__title">
                {current.title}
              </h3>
              <button
                className="help-modal__close"
                type="button"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </header>

            <div className="help-modal__content">
              {MODAL_CONTENT[current.key]}
            </div>

            <footer className="help-modal__footer">
              <button className="help-modal__btn" type="button" onClick={closeModal}>
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </main>
  );
};

export default HelpPage;
